import type { SystemRole } from "@contextos/shared";
import {
  CableFilledIcon,
  CableIcon,
  ChecklistFilledIcon,
  ChecklistIcon,
  Diversity3FilledIcon,
  Diversity3Icon,
  DomainIcon,
  EditNoteFilledIcon,
  EditNoteIcon,
  EventAvailableFilledIcon,
  EventAvailableIcon,
  ForumFilledIcon,
  ForumIcon,
  GroupFilledIcon,
  GroupIcon,
  HistoryFilledIcon,
  HistoryIcon,
  HomeFilledIcon,
  HomeIcon,
  HowToRegFilledIcon,
  HowToRegIcon,
  HubFilledIcon,
  HubIcon,
  LibraryBooksFilledIcon,
  LibraryBooksIcon,
  ManageAccountsFilledIcon,
  ManageAccountsIcon,
  PolicyFilledIcon,
  PolicyIcon,
  RateReviewFilledIcon,
  RateReviewIcon,
  RouteFilledIcon,
  RouteIcon,
  SchoolFilledIcon,
  SchoolIcon,
  SettingsFilledIcon,
  SettingsIcon,
  SpaceDashboardFilledIcon,
  SpaceDashboardIcon,
  TuneFilledIcon,
  TuneIcon,
  type MaterialIcon,
} from "@/components/ui/icons";
import type { Persona } from "@/features/workspace/viewer";
import { HIDDEN } from "@/lib/hidden";

/**
 * The one place navigation is defined. The rail, the bottom bar and any future command palette
 * read from here. Items are gated by the backend role of the current workspace membership, never
 * by a client-side notion of "who the user is".
 *
 * Add a screen here when it ships. Do not add links to screens that do not exist yet.
 */
export interface NavItem {
  id: string;
  label: string;
  /** Short label for the collapsed rail and the bottom bar. */
  short?: string;
  icon: MaterialIcon;
  /** Filled variant shown while the destination is active (M3 FILL 1). */
  activeIcon: MaterialIcon;
  to: string;
  /** Match only the exact path (for the home item). */
  end?: boolean;
  /** Other path sections that belong to this destination, e.g. project pages under the Knowledge Hub. */
  activePaths?: readonly string[];
  /** Roles that see this item. Omit for everyone. */
  roles?: readonly SystemRole[];
  /** Personas whose rail has this item, as in the prototype's "View as". Omit for every persona. */
  personas?: readonly Persona[];
  /** Also shown to path curators, whatever their role and persona (not guests). */
  alsoForCurators?: boolean;
  /** Also shown to owners, who see everything (People Ops pages and the directory). */
  alsoForOwner?: boolean;
}

export const NAV_ITEMS: readonly NavItem[] = [
  {
    id: "home",
    label: "Home",
    icon: HomeIcon,
    activeIcon: HomeFilledIcon,
    to: "/",
    end: true,
    personas: ["employee", "manager", "people_ops"],
  },
  // The prototype labels the admin's Home "Overview".
  {
    id: "overview",
    label: "Overview",
    icon: SpaceDashboardIcon,
    activeIcon: SpaceDashboardFilledIcon,
    to: "/",
    end: true,
    personas: ["admin"],
  },
  {
    id: "roadmap",
    label: "My roadmap",
    short: "Roadmap",
    icon: RouteIcon,
    activeIcon: RouteFilledIcon,
    to: "/roadmap",
    // Guests aren't onboarding: their Home lists their projects instead.
    roles: ["owner", "admin", "manager", "hr", "member"],
    personas: ["employee"],
  },
  {
    id: "team",
    label: "Team onboarding",
    short: "Team",
    icon: Diversity3Icon,
    activeIcon: Diversity3FilledIcon,
    to: "/team",
    personas: ["manager"],
  },
  {
    id: "onboarding",
    label: "Onboarding",
    icon: HowToRegIcon,
    activeIcon: HowToRegFilledIcon,
    to: "/onboarding",
    roles: ["hr"],
    personas: ["people_ops"],
    alsoForOwner: true,
  },
  {
    id: "probation",
    label: "Probation reviews",
    short: "Probation",
    icon: EventAvailableIcon,
    activeIcon: EventAvailableFilledIcon,
    to: "/probation",
    roles: ["hr"],
    personas: ["people_ops"],
    alsoForOwner: true,
  },
  {
    id: "templates",
    label: "Templates",
    icon: LibraryBooksIcon,
    activeIcon: LibraryBooksFilledIcon,
    to: "/templates",
    // Admins publish templates too; managers and path curators draft theirs for review (N-18).
    roles: ["hr", "admin", "manager"],
    personas: ["people_ops", "admin", "manager"],
    alsoForOwner: true,
    alsoForCurators: true,
  },
  {
    id: "tutorials",
    label: "Tutorials",
    icon: SchoolIcon,
    activeIcon: SchoolFilledIcon,
    to: "/tutorials",
    roles: ["owner", "admin", "hr"],
    personas: ["admin", "people_ops"],
    // Path curators build tutorials for their projects whatever their role.
    alsoForCurators: true,
  },
  {
    id: "members",
    label: "Members & roles",
    short: "Members",
    icon: ManageAccountsIcon,
    activeIcon: ManageAccountsFilledIcon,
    to: "/members",
    // People Ops runs departments and teams here; roles stay with owners and admins.
    roles: ["owner", "admin", "hr"],
    personas: ["admin", "people_ops"],
  },
  {
    id: "connectors",
    label: "Connectors",
    icon: CableIcon,
    activeIcon: CableFilledIcon,
    to: "/connectors",
    roles: ["owner", "admin"],
    personas: ["admin"],
  },
  {
    id: "access",
    label: "Access policies",
    short: "Access",
    icon: PolicyIcon,
    activeIcon: PolicyFilledIcon,
    to: "/access",
    roles: ["owner", "admin"],
    personas: ["admin"],
  },
  {
    id: "ai",
    label: "AI settings",
    short: "AI",
    icon: TuneIcon,
    activeIcon: TuneFilledIcon,
    to: "/ai",
    roles: ["owner", "admin"],
    personas: ["admin"],
  },
  {
    id: "audit",
    label: "Audit log",
    short: "Audit",
    icon: HistoryIcon,
    activeIcon: HistoryFilledIcon,
    to: "/audit",
    // HR sees the People Ops trail only; the API narrows it (see GET /audit).
    roles: ["owner", "admin", "hr"],
    personas: ["admin", "people_ops"],
  },
  {
    id: "workspace",
    label: "Workspace",
    icon: DomainIcon,
    activeIcon: DomainIcon,
    to: "/workspace",
    roles: ["owner", "admin"],
    personas: ["admin"],
  },
  // The prototype has no Projects item: projects are a collection inside the Knowledge Hub.
  {
    id: "knowledge",
    label: "Knowledge Hub",
    short: "Knowledge",
    icon: HubIcon,
    activeIcon: HubFilledIcon,
    to: "/knowledge",
    activePaths: ["projects"],
  },
  {
    id: "reviews",
    label: "Review queue",
    short: "Reviews",
    icon: RateReviewIcon,
    activeIcon: RateReviewFilledIcon,
    to: "/reviews",
    personas: ["manager"],
  },
  {
    id: "people",
    label: "People",
    icon: GroupIcon,
    activeIcon: GroupFilledIcon,
    to: "/people",
    // The directory; guests cannot read it (API 403). Admins manage people from "Members & roles";
    // owners also browse it, as they see everything.
    roles: ["owner", "admin", "manager", "hr", "member"],
    personas: ["employee", "manager", "people_ops"],
    alsoForOwner: true,
  },
  {
    id: "contributions",
    label: "Contributions",
    short: "Contribute",
    icon: EditNoteIcon,
    activeIcon: EditNoteFilledIcon,
    to: "/contributions",
    personas: ["employee"],
    roles: ["member"],
  },
  {
    id: "learning",
    label: "My learning",
    short: "Learning",
    icon: RouteIcon,
    activeIcon: RouteFilledIcon,
    to: "/learning",
    // Managers and People Ops: the pages they have besides their team and onboarding work.
    roles: ["manager", "hr"],
    personas: ["manager", "people_ops"],
  },
  {
    id: "conversations",
    label: "Member conversations",
    short: "Conversations",
    icon: ForumIcon,
    activeIcon: ForumFilledIcon,
    to: "/conversations",
    // Owners read what members asked Context Bot; each read is audited.
    roles: ["owner"],
    personas: ["admin"],
  },
  {
    id: "setup",
    label: "Workspace setup",
    short: "Setup",
    icon: ChecklistIcon,
    activeIcon: ChecklistFilledIcon,
    to: "/setup",
    roles: ["owner", "admin"],
  },
];

/** Pinned to the bottom of the rail for everyone (prototype `.rail-bottom`); on phones it is in the account menu. */
export const SETTINGS_ITEM: NavItem = {
  id: "settings",
  label: "Settings",
  icon: SettingsIcon,
  activeIcon: SettingsFilledIcon,
  to: "/settings",
};

/** True when the path is inside one of the item's extra sections (works under any route prefix). */
export function isInActivePaths(item: NavItem, pathname: string): boolean {
  return (item.activePaths ?? []).some((section) =>
    new RegExp(`(^|/)${section}(/|$)`).test(pathname),
  );
}

/** The owner's rail: the admin's, then the people pages owners also see, before setup. */
const OWNER_ORDER: readonly string[] = [
  "overview",
  "tutorials",
  "members",
  "connectors",
  "access",
  "ai",
  "audit",
  "workspace",
  "knowledge",
  "people",
  "onboarding",
  "probation",
  "templates",
  "conversations",
  "setup",
];

/** Rails whose order differs from `NAV_ITEMS`, as in the prototype: People Ops puts People first. */
const PERSONA_ORDER: Partial<Record<Persona, readonly string[]>> = {
  people_ops: [
    "home",
    "onboarding",
    "probation",
    "templates",
    "people",
    "members",
    "knowledge",
    "learning",
    "audit",
  ],
};

/** The rail for a role and persona (see useViewer). An unknown role or persona hides gated items. */
export function navItemsFor(
  role: SystemRole | undefined,
  persona?: Persona,
  { curator = false }: { curator?: boolean } = {},
): NavItem[] {
  const items = NAV_ITEMS.filter(
    (item) =>
      !(HIDDEN.contributions && item.id === "contributions") &&
      ((curator && item.alsoForCurators === true && role !== "guest") ||
        (role === "owner" && item.alsoForOwner === true) ||
        ((!item.roles || (role !== undefined && item.roles.includes(role))) &&
          (!item.personas || (persona !== undefined && item.personas.includes(persona))))),
  );
  const order =
    role === "owner" && persona === "admin"
      ? OWNER_ORDER
      : persona
        ? PERSONA_ORDER[persona]
        : undefined;
  if (!order) return items;
  const rank = (item: NavItem) => {
    const index = order.indexOf(item.id);
    return index < 0 ? order.length : index;
  };
  return [...items].sort((a, b) => rank(a) - rank(b));
}

/** The bottom bar holds at most four destinations (M3). */
export const BOTTOM_NAV_LIMIT = 4;
